import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

// PrimeNG imports
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputNumberModule } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { CheckboxModule } from 'primeng/checkbox';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';

// PrimeNG services
import { MessageService } from 'primeng/api';

// Services and models
import { PaymentConfigService } from './services/payment-config.service';
import {
  PaymentConfigItem,
  PAYMENT_METHOD_OPTIONS,
} from './payment-config.config';

// Shared components
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';

@Component({
  selector: 'app-payment-config',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TranslateModule,
    SelectModule,
    MultiSelectModule,
    InputNumberModule,
    ButtonModule,
    ToastModule,
    TagModule,
    TableModule,
    CheckboxModule,
    ToggleSwitchModule,
    TooltipModule,
    AppButtonComponent,
  ],
  providers: [MessageService],
  templateUrl: './payment-config.component.html',
  styleUrls: ['./payment-config.component.css'],
})
export class PaymentConfigComponent implements OnInit {
  // Signals for reactive state management
  selectedSection = signal<string>('payment-methods');
  selectedPaymentMethod = signal<string>('');
  loading = signal<boolean>(false);

  // Payment methods list for sidebar
  paymentMethods = signal<PaymentConfigItem[]>([]);

  // Payment configuration data
  paymentConfig = {
    partner_name: '',
    method_name: '',
    store_Id: '',
    isDisable: false,
    test: false,
  };

  // Options for dropdowns
  paymentMethodOptions = PAYMENT_METHOD_OPTIONS;

  constructor(
    private readonly paymentConfigService: PaymentConfigService,
    private readonly messageService: MessageService,
    private readonly translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  /**
   * Load payment configurations
   */
  loadData(): void {
    this.loading.set(true);
    // Load payment methods for sidebar
    this.paymentConfigService
      .getPaymentConfigs({ page: 1, size: 100 })
      .subscribe({
        next: (response) => {
          this.paymentMethods.set(response.data);
          // Select first payment method by default
          if (response.data && response.data.length > 0) {
            this.selectPaymentMethod(response.data[0]);
          }
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error loading payment methods:', error);
          this.loading.set(false);
        },
      });
  }

  /**
   * Select a payment method from sidebar
   */
  selectPaymentMethod(paymentMethod: PaymentConfigItem): void {
    this.selectedPaymentMethod.set(paymentMethod.method_name);
    this.paymentConfig = {
      partner_name: paymentMethod.partner_name,
      method_name: paymentMethod.method_name,
      store_Id: paymentMethod.store_Id,
      isDisable: paymentMethod.isDisable,
      test: paymentMethod.test,
    };
  }

  /**
   * Toggle payment method status (enable/disable)
   */
  togglePaymentMethodStatus(
    paymentMethod: PaymentConfigItem | any,
    isEnabled: boolean
  ): void {
    // Update the payment method in the list
    const updatedMethods = this.paymentMethods().map((method) =>
      method.access_key === paymentMethod.access_key
        ? { ...method, isDisable: !isEnabled }
        : method
    );
    this.paymentMethods.set(updatedMethods);

    // Update the form configuration
    this.paymentConfig = {
      ...this.paymentConfig,
      isDisable: !isEnabled,
    };

    // Show success message
    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('common.messages.success'),
      detail: isEnabled
        ? this.translateService.instant(
            'payment_config.messages.method_enabled'
          )
        : this.translateService.instant(
            'payment_config.messages.method_disabled'
          ),
    });
  }

  /**
   * Get toggle switch style to override PrimeNG default colors
   */
  getToggleSwitchStyle() {
    return {
      '--p-toggleswitch-checked-background': '#3b82f6',
      '--p-toggleswitch-checked-border-color': '#3b82f6',
      '--p-toggleswitch-checked-hover-background': '#2563eb',
      '--p-toggleswitch-checked-hover-border-color': '#2563eb',
    };
  }

  /**
   * Get display name for payment method
   */
  getPaymentMethodDisplayName(methodName: string): string {
    const method = this.paymentMethodOptions.find(
      (option) => option.value === methodName
    );
    return method ? this.translateService.instant(method.label) : methodName;
  }

  /**
   * Handle form reset
   */
  onReset(): void {
    this.paymentConfig = {
      partner_name: '',
      method_name: '',
      store_Id: '',
      isDisable: false,
      test: false,
    };
    this.selectedPaymentMethod.set('');
  }

  /**
   * Handle form save
   */
  onSave(): void {
    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('common.messages.success'),
      detail: this.translateService.instant(
        'payment_config.messages.save_success'
      ),
    });
  }

  /**
   * Get status severity for tag
   */
  getStatusSeverity(isDisable: boolean): 'success' | 'secondary' {
    return !isDisable ? 'success' : 'secondary';
  }

  /**
   * Get status display text
   */
  getStatusDisplayText(isDisable: boolean): string {
    return !isDisable
      ? this.translateService.instant('common.table.status.active')
      : this.translateService.instant('common.table.status.inactive');
  }

  /**
   * Get status style for tag
   */
  getStatusStyle(isDisable: boolean): any {
    return !isDisable
      ? {
          'background-color': '#ecfdf5',
          color: '#065f46',
          border: '1px solid #d1fae5',
        }
      : {
          'background-color': '#f9fafb',
          color: '#6b7280',
          border: '1px solid #e5e7eb',
        };
  }

  /**
   * Get test mode severity for tag
   */
  getTestModeSeverity(test: boolean): 'success' | 'secondary' {
    return test ? 'success' : 'secondary';
  }

  /**
   * Get test mode display text
   */
  getTestModeDisplayText(test: boolean): string {
    return test
      ? this.translateService.instant('payment_config.test_mode.enabled')
      : this.translateService.instant('payment_config.test_mode.disabled');
  }

  /**
   * Get translated payment method options
   */
  getTranslatedPaymentMethodOptions(): any[] {
    return this.paymentMethodOptions.map((option) => ({
      value: option.value,
      label: this.translateService.instant(option.label),
    }));
  }
}
